import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { API_USER_SHIFTS } from "../../config/Endpoints.js";
import type { SelectOption } from "../components/ui/select/select.tsx";
import { ApiError, getApi } from "../utils/api.ts";
import { accessToken } from "../utils/auth.ts";

const companyShiftRowSchema = z.object({
  companyName: z.string(),
  shiftType: z.string(),
});

const ALLOWED_COMPANY_SHIFT_TYPES = new Set([
  "CCI_US",
  "CCI_INDIA",
  "IRED_INDIA",
  "IVYCLINIC",
  "IVY_CLINIC",
  "SQE",
]);

function unwrapRows(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (payload != null && typeof payload === "object") {
    const data = (payload as Record<string, unknown>).data;
    if (Array.isArray(data)) return data;
  }
  return [];
}

export function toCompanyNameOptions(payload: unknown): SelectOption[] {
  const parsed = z.array(companyShiftRowSchema).safeParse(unwrapRows(payload));
  if (!parsed.success) return [];

  return parsed.data
    .filter((row) => ALLOWED_COMPANY_SHIFT_TYPES.has(row.shiftType.toUpperCase()))
    .map((row) => ({ value: row.shiftType, label: row.companyName }));
}

export function useCompanyNameOptions() {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getApi<unknown>(
        API_USER_SHIFTS,
        accessToken(),
        "json",
        signal,
      );
      setOptions(toCompanyNameOptions(data));
    } catch (cause) {
      if (signal?.aborted) return;
      setOptions([]);
      setError(
        cause instanceof ApiError
          ? cause.message
          : "Failed to load company names.",
      );
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refetch(controller.signal);
    return () => controller.abort();
  }, [refetch]);

  return { options, loading, error, refetch };
}
